/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe } from '@codegrade/maybe';
import * as utils from '@/utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class TooDeep extends Error {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class Executor {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    calls = 0;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    maxCalls = 10_000;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _exact;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    starMatchesHidden;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(opts) {
        this._exact = opts.exact;
        this.starMatchesHidden = opts.starMatchesHidden;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get exact() {
        return this._exact;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    check() {
        if (this.calls > this.maxCalls) {
            throw new TooDeep('Too many recursive calls');
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    recurse(glob, path, exact) {
        this.calls += 1;
        this.check();
        const origExact = this.exact;
        try {
            if (exact != null) {
                this._exact = exact;
            }
            return glob.matches(this, path);
        }
        finally {
            this._exact = origExact;
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function* traverseList(p) {
    let cur = p;
    while (cur.isJust()) {
        const next = cur.extract();
        yield next;
        cur = next.parent;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class FilenameGlob {
    parts;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(parts) {
        this.parts = parts;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static make(name) {
        utils.AssertionError.assert(name !== '', 'A filename cannot be empty.');
        const parts = [];
        let nextRaw = false;
        let cur = [];
        if (name === '*') {
            return new FilenameGlob([]);
        }
        for (const char of name) {
            if (nextRaw) {
                cur.push(char);
            }
            else if (char === '\\') {
                nextRaw = true;
            }
            else if (char === '*') {
                parts.push(cur.join(''));
                cur = [];
            }
            else {
                cur.push(char);
            }
        }
        parts.push(cur.join(''));
        utils.AssertionError.assert(parts.length < 3, 'At most one glob is allowed in a filename.');
        return new FilenameGlob(parts);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    matches(executor, file) {
        const name = file.name;
        switch (this.parts.length) {
            case 0:
                return executor.starMatchesHidden || name[0] !== '.';
            case 1:
                return name === this.parts[0];
            case 2: {
                const [part1, part2] = this.parts;
                if (part1 === '' && !executor.starMatchesHidden && name[0] === '.') {
                    return false;
                }
                return name.startsWith(part1) && name.slice(part1.length).endsWith(part2);
            }
            default:
                return utils.AssertionError.assertNever(this.parts);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toString() {
        switch (this.parts.length) {
            case 0:
                return '*';
            case 1:
            case 2:
                return this.parts.join('*');
            default:
                return utils.AssertionError.assertNever(this.parts);
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class GlobPart {
    parent;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    containsWildcard;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    minFilesNeeded;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(parent) {
        this.parent = parent;
        this.containsWildcard = this._calcContainsWildcard();
        this.minFilesNeeded = this._calcMinFilesNeeded();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    matches(executor, path) {
        const amountFiles = path.map(utils.attr('depth')).orDefault(0);
        const tooManyFiles = amountFiles - this.minFilesNeeded;
        // We don't have enough files, can only match in non exact
        // mode.
        if (tooManyFiles < 0) {
            if (executor.exact) {
                return false;
            }
            return this.parent.mapOrDefault(parent => executor.recurse(parent, path, false), false);
        }
        // In non exact mode everything that comes before a wildcard
        // can always be ignored.
        if (!executor.exact &&
            this.containsWildcard &&
            this.parent.isJust() &&
            this.parent.extract().containsWildcard) {
            return executor.recurse(this.parent.extract(), path, false);
        }
        // We know exact how many files we need, and we got too many,
        // this can never match.
        if (!this.containsWildcard && tooManyFiles > 0) {
            return false;
        }
        return this._matches(executor, path);
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get isWildcard() {
        return false;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _calcContainsWildcard() {
        if (this.isWildcard) {
            return true;
        }
        return this.parent.mapOrDefault(x => x.containsWildcard, false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _calcMinFilesNeeded() {
        const base = this.parent.map(x => x.minFilesNeeded).orDefault(0);
        return base + (this.isWildcard ? 0 : 1);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static make(data, isDir, parent) {
        if (data === '**') {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return new FullGlob(parent);
        }
        else if (isDir) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return new Directory(FilenameGlob.make(data), parent);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return new File(FilenameGlob.make(data), parent);
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class Directory extends GlobPart {
    name;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, parent) {
        super(parent);
        this.name = name;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    copy(parent) {
        return new Directory(this.name, parent);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _matches(executor, path) {
        return path.mapOrDefault(cur => cur.isDir &&
            this.name.matches(executor, cur) &&
            this.parent.mapOrDefault(p => executor.recurse(p, cur.parent), 
            // Prev part is not required if we are at the root.
            cur.parent.isNothing()), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toStringList() {
        const res = this.parent.mapOrDefault(p => p.toStringList(), ['']);
        res.push(`${this.name.toString()}`);
        return res;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class FullGlob extends GlobPart {
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get isWildcard() {
        return true;
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    copy(parent) {
        return new FullGlob(parent);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(parent) {
        const newPrevPart = parent.chain(part => {
            if (part instanceof FullGlob) {
                return part.parent;
            }
            return Maybe.of(part);
        });
        super(newPrevPart);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _matches(executor, path) {
        return this.parent
            .map(parent => {
            const minFilesNeeded = parent.minFilesNeeded;
            if (!IS_PRODUCTION) {
                utils.AssertionError.assert(minFilesNeeded > 0);
            }
            const amount = path.mapOrDefault(p => p.depth, 0) - minFilesNeeded;
            for (const cur of utils.itertools.islice(traverseList(path), 0, amount + 1)) {
                if (executor.recurse(parent, Maybe.of(cur))) {
                    return true;
                }
                if (!executor.starMatchesHidden && cur.name.startsWith('.')) {
                    return false;
                }
            }
            return false;
        })
            .orDefaultLazy(() => {
            if (executor.starMatchesHidden) {
                return true;
            }
            return utils.itertools.all(traverseList(path), el => !el.name.startsWith('.'));
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toStringList() {
        return this.parent.mapOrDefault(p => {
            const res = p.toStringList();
            res.push('**');
            return res;
        }, []);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class File extends GlobPart {
    name;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(name, parent) {
        super(parent);
        this.name = name;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    copy(parent) {
        return new File(this.name, parent);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _matches(executor, path) {
        return path.mapOrDefault(cur => {
            if (cur.isDir) {
                return false;
            }
            else if (!this.name.matches(executor, cur)) {
                return false;
            }
            else {
                return this.parent.mapOrDefault(part => executor.recurse(part, cur.parent, true), 
                // Only ok if there are no parents for this
                // file.
                cur.parent.isNothing());
            }
        }, false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toStringList() {
        const res = this.parent.mapOrDefault(p => p.toStringList(), ['']);
        res.push(`${this.name.toString()}`);
        return res;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Glob {
    tailPart;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(tailPart) {
        this.tailPart = tailPart;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static make(glob) {
        const parts = [];
        const curPart = [];
        let rawNext = false;
        let toProcesss;
        if (glob[0] === '/') {
            toProcesss = glob.substring(1);
        }
        else {
            toProcesss = glob;
            parts.push('**');
        }
        for (const char of toProcesss) {
            if (rawNext) {
                curPart.push(char);
            }
            else if (char === '\\') {
                rawNext = true;
            }
            else if (char === '/') {
                parts.push(curPart.join(''));
                curPart.splice(0, curPart.length);
            }
            else {
                curPart.push(char);
            }
        }
        let lastIsDir = true;
        if (curPart.length > 0) {
            parts.push(curPart.join(''));
            lastIsDir = false;
        }
        return parts
            .reduce((acc, part, idx) => {
            const isDir = idx + 1 < parts.length || lastIsDir;
            return Maybe.of(GlobPart.make(part, isDir, acc));
        }, Maybe.empty())
            .map(tailPart => new Glob(tailPart))
            .orDefaultLazy(() => Glob.default());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toString() {
        return this.tailPart.toStringList().join('/');
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addHead(dirPattern) {
        const newHead = new Directory(new FilenameGlob([dirPattern]), Maybe.empty());
        const newTailPart = Array.from(traverseList(Maybe.of(this.tailPart)))
            .reverse()
            .reduce((acc, cur) => cur.copy(Maybe.of(acc)), newHead);
        return new Glob(newTailPart);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static default() {
        return new Glob(new FullGlob(Maybe.empty()));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _match(path, opts) {
        try {
            return this.tailPart.matches(new Executor(opts), Maybe.of(path));
        }
        catch (e) {
            if (e instanceof TooDeep) {
                return false;
            }
            throw e;
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    match(path, args) {
        return this._match(path, args);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isDirPattern() {
        return !(this.tailPart instanceof File);
    }
}
